import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Trophy, Shield, Users, Zap, BarChart3, Camera, Building2, Sparkles } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import StatCounter from "../components/StatCounter";
import { HERO_IMAGES, LOGO_BLUE, LOGO_GOLD } from "../lib/constants";
import api from "../lib/api";

const SERVICES = [
    { icon: Trophy, title: "Athlete Brand Development", desc: "Build each athlete's identity, message, image, visual style and long-term brand position." },
    { icon: Camera, title: "Social Media Management", desc: "Plan, produce, approve, schedule and analyze athlete content across every major platform." },
    { icon: Shield, title: "NIL Deal Representation", desc: "Identify opportunities, manage negotiations, organize contracts and track payments." },
    { icon: Building2, title: "Sponsorship Acquisition", desc: "Connect athletes with brands, businesses, events and community organizations." },
    { icon: Sparkles, title: "Content Production", desc: "Photography, video, interviews, graphics, short-form and behind-the-scenes." },
    { icon: BarChart3, title: "Long-Term Business Development", desc: "Merchandise, camps, appearances, speaking, licensing and life after sports." },
];

export default function Home() {
    const [stats, setStats] = useState(null);
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/stats").then(r => setStats(r.data)).catch(() => {});
        api.get("/athletes", { params: { featured: true, limit: 3 } }).then(r => setFeatured(r.data)).catch(() => {});
    }, []);

    return (
        <PageLayout>
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="home-hero">
                <img src={HERO_IMAGES.stadium} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                <div className="absolute top-0 left-0 w-56 h-56 opacity-20 pixel-block">
                    <div className="grid grid-cols-8 gap-1 p-6">
                        {Array.from({ length: 40 }).map((_, i) => (
                            <div key={`pixel-${i}`} className="aspect-square bg-[#d4af37]" style={{ opacity: Math.random() * 0.9 + 0.1 }} />
                        ))}
                    </div>
                </div>
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="section-label mb-6 animate-rise">Athlete Branding · Social · NIL</div>
                    <h1 className="font-display font-bold text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl animate-rise" data-testid="hero-headline">
                        YOUR TALENT BUILT<br/>THE MOMENT.<br/>
                        <span className="text-[#d4af37]">DIGITAL BEAST</span> BUILDS<br/>THE EMPIRE.
                    </h1>
                    <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed animate-rise" data-testid="hero-sub">
                        Athlete branding, social media management, sponsorship development and NIL representation for high school and collegiate athletes ready to build influence beyond the game.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3 animate-rise">
                        <Link to="/apply" className="btn-gold" data-testid="hero-apply-btn">Apply for Representation <ArrowRight size={16} /></Link>
                        <Link to="/athletes" className="btn-outline" data-testid="hero-explore-btn">Explore Athletes</Link>
                        <Link to="/brands" className="btn-outline" data-testid="hero-brand-btn">Become a Brand Partner</Link>
                    </div>
                </div>
            </section>

            {/* STATS BAR */}
            <section className="border-t border-b border-white/10 bg-[#0a0a0a]" data-testid="home-stats">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-10">
                    {stats && [
                        { v: stats.athletes_represented, l: "Athletes Represented", tid: "stat-athletes" },
                        { v: stats.social_views, l: "Social Views Generated", tid: "stat-views" },
                        { v: stats.brand_partnerships, l: "Brand Partnerships", tid: "stat-partnerships" },
                        { v: stats.campaigns_completed, l: "Campaigns Completed", tid: "stat-campaigns" },
                        { v: stats.audience_growth_pct, l: "Avg. Audience Growth %", suffix: "%", tid: "stat-growth" },
                        { v: stats.community_impact, l: "Community Impact Events", tid: "stat-impact" },
                    ].map((s) => <StatCounter key={s.tid} value={s.v} label={s.l} suffix={s.suffix} testid={s.tid} />)}
                </div>
            </section>

            {/* HEADLINE CTA (Athlete callout) */}
            <section className="py-24 md:py-32" data-testid="home-athlete-callout">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="section-label mb-6">You trained for the game.</div>
                        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-white">
                            Now build the <span className="text-[#d4af37]">name</span>.
                        </h2>
                        <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-xl">
                            Digital Beast helps elite athletes build recognizable brands, grow engaged audiences and pursue strategic NIL opportunities with professional management behind them.
                        </p>
                        <div className="mt-8 flex gap-3">
                            <Link to="/apply" className="btn-gold" data-testid="athlete-cta-apply">Become a Beast</Link>
                            <Link to="/services" className="btn-outline" data-testid="athlete-cta-services">Our Services</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[4/5] surface overflow-hidden">
                            <img src={HERO_IMAGES.football} alt="Athlete" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-4 -left-4 surface p-5 w-56 hidden md:block">
                            <div className="text-xs uppercase tracking-[0.2em] text-white/40">Live Deal</div>
                            <div className="font-display text-2xl text-[#d4af37] mt-1">+$18,400</div>
                            <div className="text-xs text-white/50 mt-1">Q1 net athlete payments</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-20 md:py-28 border-t border-white/10 bg-[#0a0a0a]" data-testid="home-services">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <div className="section-label mb-5">What Digital Beast Does</div>
                            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight tracking-tight max-w-2xl">
                                Elite representation. Every angle covered.
                            </h2>
                        </div>
                        <Link to="/services" className="btn-outline self-start" data-testid="services-cta">See all services <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {SERVICES.map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="surface surface-hover p-7" data-testid={`service-card-${title.toLowerCase().replace(/\s+/g,'-')}`}>
                                <Icon className="text-[#d4af37]" size={28} strokeWidth={1.5} />
                                <div className="font-display text-2xl mt-6 text-white leading-tight">{title}</div>
                                <p className="mt-3 text-white/55 text-sm leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED ATHLETES */}
            <section className="py-20 md:py-28" data-testid="home-featured-athletes">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex justify-between items-end mb-10">
                        <div>
                            <div className="section-label mb-5">Featured Athletes</div>
                            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight">The Beasts</h2>
                        </div>
                        <Link to="/athletes" className="btn-outline hidden md:inline-flex" data-testid="featured-view-all">View directory <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featured.map(a => (
                            <Link key={a.id} to={`/athletes/${a.id}`} className="group block relative aspect-[3/4] overflow-hidden surface surface-hover" data-testid={`featured-athlete-${a.id}`}>
                                <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                <div className="absolute top-4 left-4 right-4 flex justify-between text-[10px] tracking-[0.24em] uppercase text-white/70">
                                    <span>{a.sport}</span>
                                    <span className="text-[#d4af37]">#{a.jersey}</span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-5">
                                    <div className="font-display text-3xl text-white leading-none">{a.name.toUpperCase()}</div>
                                    <div className="text-white/70 text-xs uppercase tracking-widest mt-2">{a.position} · {a.school}</div>
                                    <div className="text-[#d4af37] text-xs mt-2 font-mono">{(a.social?.followers || 0).toLocaleString()} followers</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOOTBALL MODULE */}
            <section className="py-20 md:py-28 border-t border-white/10 relative overflow-hidden" data-testid="home-football-module">
                <img src={HERO_IMAGES.football} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/50" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-7">
                            <div className="section-label mb-5">Enter Digital Beast Football</div>
                            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
                                FROM FRIDAY NIGHTS<br/>TO <span className="text-[#d4af37]">SUPER BOWL SUNDAY.</span>
                            </h2>
                            <p className="text-white/70 mt-5 max-w-xl">
                                Fantasy Football. NFL. D1. America's Top High School Programs. One Football Network.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/football" className="btn-gold" data-testid="home-football-enter">Enter Football <ArrowRight size={14}/></Link>
                                <Link to="/football/fantasy" className="btn-outline" data-testid="home-football-fantasy">Fantasy League</Link>
                                <Link to="/football/super-bowl" className="btn-outline" data-testid="home-football-sb">Super Bowl Countdown</Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                            {[
                                { l: "NFL", to: "/football/nfl" },
                                { l: "D1 College", to: "/football/college" },
                                { l: "HS Top 50", to: "/football/high-school" },
                                { l: "Road to League", to: "/football/road-to-the-league" },
                            ].map(x => (
                                <Link key={x.to} to={x.to} className="surface surface-hover p-5" data-testid={`home-football-tile-${x.l.toLowerCase().replace(/\s+/g,'-')}`}>
                                    <div className="font-display text-2xl text-white">{x.l}</div>
                                    <ArrowRight className="text-[#d4af37] mt-4" size={16} />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* NEBRASKA LAUNCH */}
            <section className="py-24 md:py-32 border-t border-white/10 relative overflow-hidden" data-testid="home-nebraska">
                <img src={HERO_IMAGES.stadium_lights} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-6">Where the Movement Begins</div>
                    <h2 className="font-display text-4xl md:text-6xl text-white leading-tight max-w-4xl">
                        THE DIGITAL BEAST MOVEMENT<br/>BEGINS IN <span className="text-[#d4af37]">NEBRASKA.</span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-white/60 text-lg leading-relaxed">
                        We're building relationships with football players, families, local businesses and supporters in the Nebraska athletic community — one player at a time.
                    </p>
                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-3">
                        {[
                            ["Request Consultation", "/contact"],
                            ["Nebraska Brand Partner", "/brands"],
                            ["Invite Us to Speak", "/contact"],
                            ["Sponsor a Local Athlete", "/brands"],
                            ["Join the Network", "/apply"],
                        ].map(([l, to]) => (
                            <Link key={l} to={to} className="btn-outline justify-center" data-testid={`nebraska-cta-${l.toLowerCase().replace(/\s+/g,'-')}`}>{l}</Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="py-24 md:py-32" data-testid="home-closing">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
                    <img src={LOGO_BLUE} alt="Digital Beast" className="h-16 md:h-24 mx-auto mb-8 opacity-90" />
                    <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl mx-auto">
                        Your performance gets attention.<br/>
                        Your <span className="text-[#d4af37]">brand builds legacy</span>.
                    </h2>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link to="/apply" className="btn-gold" data-testid="closing-apply-btn">Become a Digital Beast Athlete</Link>
                        <Link to="/brands" className="btn-outline" data-testid="closing-partner-btn">Partner With Our Athletes</Link>
                        <Link to="/contact" className="btn-outline" data-testid="closing-consult-btn">Schedule a Consultation</Link>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
